/**
 * THE LYRIC SHEET — one renderer, and the one place lyrics follow playback.
 *
 * ============================================================================
 * THREE FOLLOW MODES NOW LIVE HERE, and their priority matters.
 * ============================================================================
 * Newly generated songs may carry a timestamp for every lyric line. Those songs follow the
 * exact parsed line entry points. Older provider output may only carry section timings, so it
 * continues to follow block-by-block. Neither format claims per-word timing.
 *
 * Songs without `lyricSectionTimings` still fall back to the older section-follow behaviour:
 * the current section is approximated from the song's share of its length against the
 * section's share of the sheet. That remains honest for untimed songs, and the on-screen
 * note still says so.
 *
 * The shared component boundary stays the same in spirit: both the Studio lyrics column
 * and the song-detail dialog render through this file, so adding true timing to one
 * surface automatically adds it to the other instead of letting them drift apart.
 */
import { useEffect, useMemo, useRef } from 'react';

import { currentBlockIndex, lyricBlocks } from '../../lib/sections';
import { c } from '../../styles/theme';
import type { LyricSectionTiming, LyricTiming } from '../../types/domain';

/**
 * How much room the sheet is given.
 *
 * `dialog` — the song detail view, where the sheet is the subject of the screen.
 * `panel`  — the Studio's lyrics column, which sits beside a timeline and a tool rail.
 *
 * Two values rather than a set of spacing props, so a third surface has to choose one of
 * the two readings the design actually has instead of inventing a third rhythm.
 */
export type LyricSheetVariant = 'dialog' | 'panel';

export interface LyricSheetProps {
  /** The sheet's text. Bracketed lines are headings; blank lines are spacing. */
  lyrics: string;
  /**
   * Playback position as a share of the track, 0–1 — used ONLY for the untimed fallback.
   *
   * Songs with real line or section timings ignore this and follow `elapsedSec` instead.
   */
  at?: number | null;
  /**
   * Playback position in whole-track seconds.
   *
   * Only meaningful when timing data exists. Null/undefined means the song is not
   * the one currently loaded in the player, so the sheet stays static.
   */
  elapsedSec?: number | null;
  /** Real line timings parsed from the model's timed lyric transcript. */
  lyricTimings?: LyricTiming[];
  /** Real section timings, when the backend recovered them from preserved provider lyrics. */
  lyricSectionTimings?: LyricSectionTiming[];
  variant?: LyricSheetVariant;
  /** Class for the scrolling box. Layout only; each surface owns its own scroll region. */
  className?: string;
}

/** Per-variant spacing. The values each surface rendered before this file existed. */
const SPACING: Record<LyricSheetVariant, { gap: number; blank: number; lineHeight: number }> = {
  dialog: { gap: 18, blank: 7, lineHeight: 1.8 },
  panel: { gap: 16, blank: 6, lineHeight: 1.75 },
};

function validLyricSectionTimings(
  lyricSectionTimings: LyricSectionTiming[] | undefined,
  blockCount: number
): LyricSectionTiming[] {
  return (lyricSectionTimings ?? [])
    .filter(
      (entry) =>
        Number.isInteger(entry.block) &&
        entry.block >= 0 &&
        entry.block < blockCount &&
        Number.isFinite(entry.startSec) &&
        entry.startSec >= 0
    )
    .slice()
    .sort((a, b) => a.startSec - b.startSec || a.block - b.block);
}

function validLyricTimings(
  lyricTimings: LyricTiming[] | undefined,
  lineCount: number
): LyricTiming[] {
  return (lyricTimings ?? [])
    .filter(
      (entry) =>
        Number.isInteger(entry.line) &&
        entry.line >= 0 &&
        entry.line < lineCount &&
        Number.isFinite(entry.startSec) &&
        entry.startSec >= 0
    )
    .slice()
    .sort((a, b) => a.startSec - b.startSec || a.line - b.line);
}

function currentTimedLineIndex(lyricTimings: LyricTiming[], elapsedSec: number): number {
  if (!Number.isFinite(elapsedSec) || elapsedSec < 0 || lyricTimings.length === 0) return -1;

  for (let i = 0; i < lyricTimings.length; i += 1) {
    const entry = lyricTimings[i];
    const next = lyricTimings[i + 1];
    const endExclusive =
      typeof entry.endSec === 'number' && Number.isFinite(entry.endSec)
        ? entry.endSec
        : next?.startSec;
    if (elapsedSec >= entry.startSec && (endExclusive === undefined || elapsedSec < endExclusive)) {
      return entry.line;
    }
  }

  const last = lyricTimings[lyricTimings.length - 1];
  return elapsedSec >= last.startSec ? last.line : -1;
}

function currentTimedBlockIndex(
  lyricSectionTimings: LyricSectionTiming[],
  elapsedSec: number
): number {
  if (!Number.isFinite(elapsedSec) || elapsedSec < 0 || lyricSectionTimings.length === 0) return -1;

  for (let i = 0; i < lyricSectionTimings.length; i += 1) {
    const entry = lyricSectionTimings[i];
    const next = lyricSectionTimings[i + 1];
    const endExclusive =
      typeof entry.endSec === 'number' && Number.isFinite(entry.endSec)
        ? entry.endSec
        : next?.startSec;
    if (elapsedSec >= entry.startSec && (endExclusive === undefined || elapsedSec < endExclusive)) {
      return entry.block;
    }
  }

  const last = lyricSectionTimings[lyricSectionTimings.length - 1];
  return elapsedSec >= last.startSec ? last.block : -1;
}

export default function LyricSheet({
  lyrics,
  at = null,
  elapsedSec = null,
  lyricTimings,
  lyricSectionTimings,
  variant = 'dialog',
  className,
}: LyricSheetProps) {
  const blocks = lyricBlocks(lyrics);
  const lineCount = lyrics.split(/\r?\n/).length;
  const lineTimings = useMemo(
    () => validLyricTimings(lyricTimings, lineCount),
    [lineCount, lyricTimings]
  );
  const sectionTimings = useMemo(
    () => validLyricSectionTimings(lyricSectionTimings, blocks.length),
    [blocks.length, lyricSectionTimings]
  );
  const timedCurrentLine =
    elapsedSec === null || elapsedSec === undefined
      ? -1
      : currentTimedLineIndex(lineTimings, elapsedSec);
  const timedCurrentBlock =
    lineTimings.length > 0 || elapsedSec === null || elapsedSec === undefined
      ? -1
      : currentTimedBlockIndex(sectionTimings, elapsedSec);

  /*
    Real line or section timings suppress the approximate fallback entirely. Before the first
    timestamp there may honestly be no lyric highlighted at all.
  */
  const currentBlock =
    timedCurrentBlock >= 0
      ? timedCurrentBlock
      : lineTimings.length === 0 && sectionTimings.length === 0 && at !== null && at !== undefined
        ? currentBlockIndex(blocks, at)
        : -1;

  /*
    A single ref follows whichever unit is current: an exact line when available, otherwise
    the timed or approximate section.
  */
  const currentRef = useRef<HTMLElement | null>(null);
  const setCurrentRef = (node: HTMLElement | null) => {
    currentRef.current = node;
  };
  useEffect(() => {
    if (timedCurrentLine < 0 && currentBlock < 0) return;
    currentRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [currentBlock, timedCurrentLine]);

  const space = SPACING[variant];

  return (
    <>
      <div className={className}>
        {blocks.map((block, i) => {
          const isCurrentBlock = i === currentBlock;
          return (
            <div
              key={i}
              ref={isCurrentBlock ? setCurrentRef : undefined}
              className={`lyx-lyric-block${isCurrentBlock ? ' is-current' : ''}`}
              aria-current={isCurrentBlock ? 'true' : undefined}
            >
              {block.name && (
                <p
                  style={{
                    fontSize: 10.5,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: c.accent,
                    margin: i === 0 ? '0 0 6px' : `${space.gap}px 0 6px`,
                    fontWeight: 500,
                  }}
                >
                  {block.name}
                </p>
              )}
              {block.lines.map((line) => {
                if (!line.text.trim()) {
                  return <div key={line.line} style={{ height: space.blank }} />;
                }
                const isCurrentLine = line.line === timedCurrentLine;
                return (
                  <p
                    key={line.line}
                    ref={isCurrentLine ? setCurrentRef : undefined}
                    className={`lyx-lyric-line${isCurrentLine ? ' is-current' : ''}`}
                    aria-current={isCurrentLine ? 'true' : undefined}
                    style={{
                      fontSize: 12.5,
                      color: isCurrentLine ? c.accent : c.text2,
                      margin: 0,
                      lineHeight: space.lineHeight,
                      fontWeight: 400,
                    }}
                  >
                    {line.text}
                  </p>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Follow note intentionally disabled — the sheet carries no on-screen timing claim.
      {timedCurrentLine >= 0 ? null : timedCurrentBlock >= 0 ? (
        <p
          className="lyx-lyric-follow-note"
          style={{ fontSize: 10.5, color: c.text2, lineHeight: 1.55 }}
        >
          Following the song by section from preserved provider timings. Lyrixa still has no
          per-word timings for this song.
        </p>
      ) : null} */}
    </>
  );
}
